import type { BaseModelAttributes } from './base';
import { Resolution, AspectRatio } from './common';
import { TaskStatus } from './taskStatus';
import type { VideoGenerationType } from './videoGeneration';

export enum ResourceType {
  START_IMAGE = 'start_image',        // 开始帧图片
  END_IMAGE = 'end_image',            // 结束帧图片
  STORYBOARD_IMAGE = 'storyboard_image',  // 分镜图片
  VIDEO = 'video',                    // 视频
  SCRIPT_AUDIO = 'script_audio',      // 配音
  SOUND_EFFECT_AUDIO = 'sound_effect_audio',  // 音效
  LIP_SYNC_VIDEO = 'lip_sync_video',  // 对口型视频
  AUDIO_EDIT = 'audio_edit'           // 音频编辑（变声）
}

// ⭐ 统一使用 TaskStatus，保留 ResourceStatus 作为类型别名
export type ResourceStatus = TaskStatus;
export const ResourceStatus = {
  PENDING: TaskStatus.PENDING,
  GENERATING: TaskStatus.PROCESSING,  // generating → processing
  COMPLETED: TaskStatus.COMPLETED,
  FAILED: TaskStatus.FAILED
} as const;

// ⭐ ResourceStatus 到 TaskStatus 的映射（用于数据库读取）
export function resourceStatusToTaskStatus(status: string): TaskStatus {
  const mapping: Record<string, TaskStatus> = {
    'pending': TaskStatus.PENDING,
    'generating': TaskStatus.PROCESSING,
    'completed': TaskStatus.COMPLETED,
    'failed': TaskStatus.FAILED
  };
  return mapping[status] || TaskStatus.PENDING;
}

// ⭐ TaskStatus 到 ResourceStatus 的映射（用于向后兼容）
export function taskStatusToResourceStatus(status: TaskStatus): string {
  const mapping: Record<TaskStatus, string> = {
    [TaskStatus.PENDING]: 'pending',
    [TaskStatus.SUBMITTED]: 'generating',
    [TaskStatus.PROCESSING]: 'generating',
    [TaskStatus.COMPLETED]: 'completed',
    [TaskStatus.FAILED]: 'failed'
  };
  return mapping[status] || 'pending';
}

export enum ImageGenerationMode {
  TEXT_TO_IMAGE = 'text_to_image',      // 模式1：文生图 (ImageGeneration)
  IMAGE_TO_IMAGE = 'image_to_image'     // 模式2：图生图 (ImageGeneration)
}

export interface IVideoClipResource extends BaseModelAttributes {
  videoClipId: string;
  userId: string;
  resourceType: ResourceType;
  imageGenerationId?: string;
  imageEditId?: string;
  videoGenerationId?: string;
  audioGenerationId?: string;
  status: ResourceStatus;
  resultUrl?: string;
  errorMessage?: string;
  generationParams?: any;
}

export interface ImageGenerationParams {
  prompt: string;
  modelId: string;
  referenceImages?: string[];
  resolution?: Resolution;
  aspectRatio?: AspectRatio;
  autoWriteBack?: boolean;
}

export interface AudioGenerationParams {
  prompt: string;
  spkAudioUrl: string;
  emoAudioUrl?: string;
  emoAlpha?: number;
  emoText?: string;
  matchReferenceDuration?: boolean;
  speedFactor?: number;
  modelId: string;
  autoWriteBack?: boolean;
}

export interface VideoGenerationParams {
  mode?: VideoGenerationType; // 显式生成模式；缺省时仅用于兼容旧调用并按 start/endImage 推断
  prompt: string;
  modelId: string;
  duration?: number;
  resolution?: Resolution;
  aspectRatio?: AspectRatio;
  imageUrls?: string[]; // 多图/首尾帧的完整输入列表
  startImage?: string;
  endImage?: string;
  referenceAudio?: string;
  referenceVideo?: string;  // 参考视频URL
  isStoryboardGuide?: boolean;  // 是否使用分镜图片作为引导
  autoWriteBack?: boolean;
}

export interface LipSyncParams {
  videoUrl: string;
  audioUrl: string;
  modelId: string;
  autoWriteBack?: boolean;
}

export interface AudioEditingParams {
  srcAudioUrl: string;
  modelId: string;
  rvcModelId: string;
  pitch: number;
  indexRate: number;
  f0Method?: string;
  autoWriteBack?: boolean;
}